import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function AgentsTaughtMe() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="My AI Agents Just Taught Me a Better Way to Ship Software"
            date="March 16, 2026"
            readTime="8 MIN"
            heroImage="/images/blog/agents-taught-me-hero.jpg"
            description="I've been building with AI agents for months. I thought I was the engineer and they were the tools. Then one of them said two words I'd never heard, and I realized I had the relationship backwards."
        >
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I've been building with AI agents for months. I thought I understood the dynamic: I give instructions, agents execute. I'm the engineer. They're the tools.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Today that assumption got quietly dismantled — and I'm a better builder for it.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The project: fixing how Vito handles email</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Vito is our client success agent at RoofClaw. He's the guy who handles inbound leads, onboards clients, collects credentials, and shepherds every new client from "just bought" to "agent is live." He runs on a RoofClaw — the same dedicated AI hardware we sell.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                His email runs through <a href="https://agentmail.to" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:text-[#a48fff] transition-colors underline underline-offset-4">AgentMail.to</a> — an API-first email platform built specifically for AI agents. Unlike Gmail or Outlook, AgentMail is designed for programmatic inboxes, webhook-driven processing, and high-volume agent use. It's been great for outbound. Inbound was a different story.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Two specific failures kept happening:
            </p>

            <ul className="mb-8 space-y-4 text-[#F0EFF4]/70 text-base leading-relaxed pl-4">
                <li className="flex gap-3">
                    <span className="text-[#7B61FF] font-mono text-sm mt-1">01</span>
                    <span><strong className="text-[#F0EFF4]/90">Thread blindness.</strong> Vito was reacting to individual emails without reading the full thread. When I replied-all to a client, he couldn't see it. He'd re-ask me to handle something I'd already handled. Every time it happened, he wasted my time and looked unreliable to the client.</span>
                </li>
                <li className="flex gap-4">
                    <span className="text-[#7B61FF] font-mono text-sm mt-1">02</span>
                    <span><strong className="text-[#F0EFF4]/90">Attachment loss.</strong> Clients were emailing credential files — API keys, JSON config files — and Vito couldn't download them. The files would sit in the inbox, the PW Pusher links in the same email would expire in 7 days, and the build would stall. We'd have to go back to the client and ask them to resubmit. Not a great experience.</span>
                </li>
            </ul>

            <img
                src="/images/blog/agents-taught-me-inbox.jpg"
                alt="Email thread chaos becoming organized streams"
                className="w-full rounded-2xl my-12 object-cover"
                style={{ maxHeight: '400px' }}
            />

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Real problems. Real clients affected. I wrote it up as a GitHub issue — full spec, architecture, acceptance criteria — and handed it to Devin, our engineering agent.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">Two words I didn't know</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A few hours after I handed off the issue, Devin posted an update.
            </p>

            <p className="mb-6 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                "Starting Day 1 spike."
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                And then he got to work.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I had no idea what a spike was. I didn't want to interrupt Devin mid-task. So I asked Vito.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What a spike actually is</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A spike is a time-boxed exploration you run <em>before</em> you commit to building. The name comes from "driving a spike through the problem" — you're not writing production code, you're poking at the riskiest assumptions in your plan to see if they hold.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                For Devin and the AgentMail.to project, that meant:
            </p>

            <ul className="mb-8 space-y-3 text-[#F0EFF4]/70 text-base leading-relaxed pl-4">
                <li className="flex gap-3"><span className="text-[#7B61FF]">—</span> Can he actually register a webhook with AgentMail.to and receive events?</li>
                <li className="flex gap-3"><span className="text-[#7B61FF]">—</span> Does the Svix signature verification work end-to-end?</li>
                <li className="flex gap-3"><span className="text-[#7B61FF]">—</span> Does the thread API return what the docs say it returns, in the format the architecture assumes?</li>
                <li className="flex gap-3"><span className="text-[#7B61FF]">—</span> Does the attachment download endpoint actually return file data — or does it 404 like it did for Vito?</li>
            </ul>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                He's answering "will this architecture actually work?" before investing days building it out. If any of those assumptions are wrong, he finds out today — in an afternoon — not after a week of code that has to be torn out.
            </p>

            <img
                src="/images/blog/agents-taught-me-spike.jpg"
                alt="Focused spike of light piercing through complexity"
                className="w-full rounded-2xl my-12 object-cover"
                style={{ maxHeight: '420px' }}
            />

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Think about how this applies in roofing. Before you commit a crew and materials to a reroofing job, you go up on the roof and check the deck. You're not building yet. You're proving out the riskiest unknown. Decking spike. Same idea.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">Why he did it without being told</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I didn't tell Devin to do a spike. I wrote a spec, handed it to him, and he decided how to approach it.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                This is what separates an agent that executes from an agent that engineers. Devin has software development best practices baked in. He knows that jumping into production code with unverified assumptions is how projects get rebuilt twice. He's seen it happen in his training data a thousand times. The spike is automatic for him in a way it wasn't automatic for me.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I've been a business builder for years. I know how to move fast, close deals, and run teams. I did not know what a spike was. My agent did.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">When to call for a spike in your own agentic work</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Since learning this, I've started using it as a mental filter. Before I hand any significant build task to an agent, I ask: <em>what are the three things that could make this not work?</em> If I can't answer that confidently, I ask the agent to spike those assumptions before building.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Spike candidates at RoofClaw now include:
            </p>

            <ul className="mb-8 space-y-3 text-[#F0EFF4]/70 text-base leading-relaxed pl-4">
                <li className="flex gap-3"><span className="text-[#7B61FF]">—</span><span><strong className="text-[#F0EFF4]/90">New integrations.</strong> Before building a ProLine or AccuLynx connector, spike the API. Confirm it does what the docs say — not what you assume it does.</span></li>
                <li className="flex gap-3"><span className="text-[#7B61FF]">—</span><span><strong className="text-[#F0EFF4]/90">New infrastructure.</strong> Webhooks, new DB schemas, third-party auth flows. Any layer you haven't touched in this stack before.</span></li>
                <li className="flex gap-3"><span className="text-[#7B61FF]">—</span><span><strong className="text-[#F0EFF4]/90">Client-specific setups.</strong> Before the delivery call, have the agent spend 30 minutes spiking the client's tools — confirm what's actually accessible, what the API returns, where the gaps are.</span></li>
            </ul>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                An afternoon of proof beats a week of rework. Every time.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The thing nobody tells you about working with agents</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I've been building with AI for long enough that I've stopped being surprised by what agents can do. They write code, review PRs, manage inboxes, run pipelines, onboard clients. I know that now.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                What I didn't see coming was this: the best agents know things I don't. Not just facts — methodologies. Engineering disciplines. Product practices that I've spent my career adjacent to but never formally learned.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                If I'd stayed in "I'm the engineer, they're the tools" mode, I would have skimmed past Devin's update. Seen "Day 1 spike" and assumed it was status jargon. Moved on.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Instead I asked. And I learned something.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                The agents don't just do the work. They carry knowledge the founder never had. Stay curious enough to notice.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What I'm doing differently now</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I'm treating my agents less like tools and more like team members who happen to have very different training than me. When they make a decision I don't recognize, I ask about it. Not to second-guess them — to understand it.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I've started building spikes into how I brief engineering work. "Before you start this, what are the three things that could make this not work? Go prove those out first." That question is now in my vocabulary.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                And I'm paying closer attention. Not to catch mistakes — to learn from the judgment calls my agents make when I'm not watching.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I'm a better agentic engineer today than I was this morning. Vito and Devin did that.
            </p>

            <p className="text-[#F0EFF4]/60 text-base leading-relaxed">
                Devin is building the AgentMail.to inbox orchestration fix now. Vito is managing client delivery. The spike is done — assumptions verified. The build starts tomorrow.
                {' '}If you're building with agents and want to follow along, I post these builds publicly as they happen.
            </p>
        </BlogLayout>
    );
}
